import {
  coveragesForCountry,
  customFieldDefaults,
  SUPPORTED_STORE_COUNTRIES,
  withCoverageFields,
  defaultFieldsStyle,
  type BorderMode,
  type BorderPosition,
  type ButtonIcon,
  type CoverageField,
  type CustomFieldType,
  type FormConfig,
} from '@cod/config-schema';
import { Box, Button, Select, Text } from '@nimbus-ds/components';
import { PlusCircleIcon } from '@nimbus-ds/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CheckboxControl,
  CollapsibleSection,
  ColorControl,
  NumberControl,
  RadioGroupControl,
  SelectControl,
  SliderControl,
  TextControl,
} from '../components/controls.js';
import { useAppState } from '../state/AppState.js';
import { AddFieldModal } from './AddFieldModal.js';
import { useFieldProblem } from './ProblemsContext.js';
import { FieldList, HeaderCard } from './FieldEditor.js';

type Update = (updater: (prev: FormConfig) => FormConfig) => void;

interface TabProps {
  config: FormConfig;
  update: Update;
}

export function ButtonTab({ config, update }: TabProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const b = config.button;
  const setB = (patch: Partial<FormConfig['button']>) =>
    update((c) => ({ ...c, button: { ...c.button, ...patch } }));
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <TextControl
        id="btn-text"
        label={t('designer.button.text')}
        value={b.text}
        error={problem('button.text')}
        onChange={(text) => setB({ text })}
      />
      <Box display="flex" gap="4" flexWrap="wrap">
        <ColorControl
          id="btn-bg"
          label={t('designer.button.bgColor')}
          value={b.bg_color}
          onChange={(bg_color) => setB({ bg_color })}
        />
        <ColorControl
          id="btn-text-color"
          label={t('designer.button.textColor')}
          value={b.text_color}
          onChange={(text_color) => setB({ text_color })}
        />
        <ColorControl
          id="btn-border-color"
          label={t('designer.button.borderColor')}
          value={b.border_color}
          onChange={(border_color) => setB({ border_color })}
        />
      </Box>
      <Box display="flex" gap="4" flexWrap="wrap">
        <NumberControl
          id="btn-radius"
          label={t('designer.button.borderRadius')}
          value={b.border_radius}
          error={problem('button.border_radius')}
          onChange={(border_radius) => setB({ border_radius })}
          max={100}
        />
        <NumberControl
          id="btn-border-w"
          label={t('designer.button.borderWidth')}
          value={b.border_width}
          error={problem('button.border_width')}
          onChange={(border_width) => setB({ border_width })}
          max={20}
        />
      </Box>
      <Box display="flex" flexDirection="column" gap="1" maxWidth="240px">
        <Text>{t('designer.button.icon')}</Text>
        <Select
          id="btn-icon"
          name="btn-icon"
          value={b.icon}
          onChange={(e) => setB({ icon: e.target.value as ButtonIcon })}
        >
          <Select.Option value="bag" label={t('designer.button.iconBag')} />
          <Select.Option value="cart" label={t('designer.button.iconCart')} />
          <Select.Option value="none" label={t('designer.button.iconNone')} />
        </Select>
      </Box>
    </Box>
  );
}

export function FieldsTab({ config, update }: TabProps): JSX.Element {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  // Key of the field just added via the modal, so its card mounts expanded for editing.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const setField = (index: number, patch: Partial<FormConfig['fields'][number]>) =>
    update((c) => ({
      ...c,
      fields: c.fields.map((f, i) => (i === index ? { ...f, ...patch } : f)),
    }));

  const moveField = (from: number, to: number) =>
    update((c) => {
      const next = [...c.fields];
      const [moved] = next.splice(from, 1);
      if (moved) next.splice(to, 0, moved);
      return { ...c, fields: next };
    });

  // Append a new custom element and open its card so the merchant edits it immediately.
  const addField = (type: CustomFieldType) => {
    const entry = customFieldDefaults(type);
    update((c) => ({ ...c, fields: [...c.fields, entry] }));
    setOpenKey(entry.key);
    setAdding(false);
  };

  // Delete a custom field (the trash icon only shows on custom cards). Keep at least one field.
  const removeField = (index: number) =>
    update((c) =>
      c.fields.length > 1 ? { ...c, fields: c.fields.filter((_, i) => i !== index) } : c,
    );

  const fs = config.fields_style ?? defaultFieldsStyle;
  const setFs = (patch: Partial<FormConfig['fields_style']>) =>
    update((c) => ({
      ...c,
      fields_style: { ...(c.fields_style ?? defaultFieldsStyle), ...patch },
    }));

  return (
    <Box display="flex" flexDirection="column" gap="4">
      {/* Form fields: drag to reorder, expand a card to edit all its options. */}
      <CollapsibleSection title={t('designer.fields.heading')}>
        {/* The header is fixed form chrome, not a reorderable field, so it sits above the list. */}
        <HeaderCard config={config} update={update} />
        <Text fontSize="caption" color="neutral-textLow">
          {t('designer.fields.reorderHint')}
        </Text>
        <FieldList
          fields={config.fields}
          config={config}
          update={update}
          openKey={openKey}
          onMove={moveField}
          onPatch={setField}
          onRemove={removeField}
        />
        <Box marginTop="2">
          <Button onClick={() => setAdding(true)}>
            <PlusCircleIcon />
            {t('designer.addField.button')}
          </Button>
        </Box>
        <AddFieldModal open={adding} onAdd={addField} onClose={() => setAdding(false)} />
      </CollapsibleSection>

      {/* Fields styling section: same layout idiom as the Button tab. */}
      <CollapsibleSection title={t('designer.fieldsStyle.heading')}>
        <CheckboxControl
          name="fs-hide-labels"
          label={t('designer.fieldsStyle.hideLabels')}
          checked={fs.hide_labels}
          onChange={(hide_labels) => setFs({ hide_labels })}
        />
        <Box display="flex" gap="4" flexWrap="wrap">
          <ColorControl
            id="fs-bg"
            label={t('designer.fieldsStyle.bgColor')}
            value={fs.bg_color}
            onChange={(bg_color) => setFs({ bg_color })}
          />
          <ColorControl
            id="fs-text"
            label={t('designer.fieldsStyle.textColor')}
            value={fs.text_color}
            onChange={(text_color) => setFs({ text_color })}
          />
          <ColorControl
            id="fs-placeholder"
            label={t('designer.fieldsStyle.placeholderColor')}
            value={fs.placeholder_color}
            onChange={(placeholder_color) => setFs({ placeholder_color })}
          />
          <ColorControl
            id="fs-border"
            label={t('designer.fieldsStyle.borderColor')}
            value={fs.border_color}
            onChange={(border_color) => setFs({ border_color })}
          />
        </Box>
        {/* This radio chooses WHETHER there is a border; it used to be labelled "Border width",
            which is now the slider below that chooses how thick it is. */}
        <RadioGroupControl<BorderMode>
          name="fs-border-mode"
          label={t('designer.fieldsStyle.borderMode')}
          value={fs.border_mode}
          options={[
            { value: 'none', label: t('designer.fieldsStyle.noBorder') },
            { value: 'border', label: t('designer.fieldsStyle.border') },
          ]}
          onChange={(border_mode) => setFs({ border_mode })}
        />
        {fs.border_mode === 'border' && (
          <>
            <Box maxWidth="240px">
              <SelectControl<BorderPosition>
                id="fs-border-position"
                label={t('designer.fieldsStyle.borderPosition')}
                value={fs.border_position}
                options={[
                  { value: 'all', label: t('designer.fieldsStyle.posAll') },
                  { value: 'top', label: t('designer.fieldsStyle.posTop') },
                  { value: 'right', label: t('designer.fieldsStyle.posRight') },
                  { value: 'bottom', label: t('designer.fieldsStyle.posBottom') },
                  { value: 'left', label: t('designer.fieldsStyle.posLeft') },
                ]}
                onChange={(border_position) => setFs({ border_position })}
              />
            </Box>
            {/* Only while there IS a border — a width control next to "No border" would offer a
                thickness for something that is not drawn. Min 1 for the same reason. */}
            <SliderControl
              label={t('designer.fieldsStyle.borderWidth')}
              value={fs.border_width}
              min={1}
              max={20}
              onChange={(border_width) => setFs({ border_width })}
            />
          </>
        )}
        <SliderControl
          label={t('designer.fieldsStyle.borderRadius')}
          value={fs.border_radius}
          max={40}
          onChange={(border_radius) => setFs({ border_radius })}
        />
        <SliderControl
          label={t('designer.fieldsStyle.shadow')}
          value={fs.shadow}
          max={40}
          onChange={(shadow) => setFs({ shadow })}
        />
        <SliderControl
          label={t('designer.fieldsStyle.sizing')}
          value={fs.sizing}
          max={40}
          onChange={(sizing) => setFs({ sizing })}
        />
      </CollapsibleSection>
    </Box>
  );
}

export function AppearanceTab({ config, update }: TabProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const a = config.appearance;
  const setA = (patch: Partial<FormConfig['appearance']>) =>
    update((c) => ({ ...c, appearance: { ...c.appearance, ...patch } }));
  // Order summary and totals styling now live in their field cards on the Fields tab.
  return (
    <Box display="flex" flexDirection="column" gap="4">
      <CollapsibleSection title={t('designer.appearance.heading')}>
        <NumberControl
          id="ap-radius"
          label={t('designer.appearance.borderRadius')}
          value={a.border_radius}
          error={problem('appearance.border_radius')}
          onChange={(border_radius) => setA({ border_radius })}
          max={100}
        />
        {/* 🚨 No "Primary color". It reached three things, each on only ONE of the two bundles, so
            whichever a merchant tested it looked inert. Those three now use the colour the other
            bundle already used — see `primary_color` in `config-schema`. Removed 2026-08-17. */}
        <Box display="flex" gap="4" flexWrap="wrap">
          <ColorControl
            id="ap-bg"
            label={t('designer.appearance.bgColor')}
            value={a.bg_color}
            onChange={(bg_color) => setA({ bg_color })}
          />
          <ColorControl
            id="ap-text"
            label={t('designer.appearance.textColor')}
            value={a.text_color}
            onChange={(text_color) => setA({ text_color })}
          />
          <ColorControl
            id="ap-accent"
            label={t('designer.appearance.accentColor')}
            value={a.accent_color}
            onChange={(accent_color) => setA({ accent_color })}
          />
        </Box>
      </CollapsibleSection>
    </Box>
  );
}

export function TextTab({ config, update }: TabProps): JSX.Element {
  const { t } = useTranslation();
  const problem = useFieldProblem();
  const setMessages = (patch: Partial<FormConfig['messages']>) =>
    update((c) => ({ ...c, messages: { ...c.messages, ...patch } }));
  // The header, order summary, shipping method, address heading, submit and consent text now
  // live in their own field cards on the Fields tab, edited inline like every other field.

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <CollapsibleSection title={t('designer.text.messagesSection')}>
        <TextControl
          id="msg-required"
          label={t('designer.text.requiredError')}
          value={config.messages.required}
          error={problem('messages.required')}
          onChange={(required) => setMessages({ required })}
        />
        <TextControl
          id="msg-generic"
          label={t('designer.text.genericError')}
          helpText={t('designer.text.genericErrorHelp')}
          value={config.messages.generic_invalid}
          error={problem('messages.generic_invalid')}
          onChange={(generic_invalid) => setMessages({ generic_invalid })}
        />
      </CollapsibleSection>
    </Box>
  );
}

/**
 * Render an ISO alpha-2 code as a country name in the admin's language ("CL" → "Chile"), so
 * the empty-coverage notice reads like a sentence. Falls back to the raw code if the runtime
 * has no `Intl.DisplayNames` or the code isn't one it knows.
 */
function countryName(code: string, language: string): string {
  try {
    return new Intl.DisplayNames([language], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * Coverage tab: pick a deliverable-region dataset. The selected coverage's id rides in the
 * config; the storefront fetches its province/city/postal data and turns the matching fields
 * into cascading dropdowns, validated at submit. "No coverage" leaves them as plain inputs.
 */
export function CoverageTab({ config, update }: TabProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const { settings } = useAppState();
  const current = config.coverage?.id ?? '';
  // Empty option => null, i.e. no coverage (plain text fields).
  // 🚨 `withCoverageFields` on the way out: picking a coverage takes ownership of its chain
  // fields, and applying it here is what keeps the live preview honest. Without it a field the
  // merchant had previously hidden stayed hidden in the draft — and so in the preview — while the
  // storefront got it back, because `normalizeFormConfig` only runs on the way in.
  const setCoverage = (id: string): void =>
    update((c) => withCoverageFields({ ...c, coverage: { ...c.coverage, id: id || null } }));

  // The country the merchant sells in, which scopes the datasets on offer — a Colombian
  // merchant has no use for the Mexican regions. Their explicit pick wins; until they make
  // one we guess from the store's API country, which is only the country the store was
  // CREATED on (see coverageConfig) — hence the override.
  const country = config.coverage?.country ?? settings?.country ?? null;
  // Changing country invalidates the current pick, so clear it rather than leave a dataset
  // from the old country selected (coveragesForCountry would otherwise keep it visible).
  const setCountry = (next: string): void =>
    update((c) => withCoverageFields({ ...c, coverage: { id: null, country: next || null } }));
  const countryOptions = [
    { value: '', label: t('designer.coverage.countryNone') },
    ...SUPPORTED_STORE_COUNTRIES.map((code) => ({
      value: code,
      label: countryName(code, i18n.language),
    })),
  ];

  const available = coveragesForCountry(country, current);
  const options = [
    { value: '', label: t('designer.coverage.none') },
    ...available.map((entry) => ({ value: entry.id, label: entry.label })),
  ];
  const selected = available.find((entry) => entry.id === current);
  const fieldLabel: Record<CoverageField, string> = {
    province: t('designer.coverage.fieldProvince'),
    city: t('designer.coverage.fieldCity'),
    postal_code: t('designer.coverage.fieldPostal'),
  };

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <CollapsibleSection title={t('designer.coverage.heading')}>
        <Text fontSize="caption" color="neutral-textLow">
          {t('designer.coverage.description')}
        </Text>
        {/* Country first: it scopes the coverage list below it. */}
        <SelectControl
          id="coverage-country"
          label={t('designer.coverage.countryLabel')}
          value={country ?? ''}
          options={countryOptions}
          onChange={setCountry}
        />
        <Text fontSize="caption" color="neutral-textLow">
          {t('designer.coverage.countryHelp')}
        </Text>
        <SelectControl
          id="coverage-id"
          label={t('designer.coverage.label')}
          value={current}
          options={options}
          onChange={setCoverage}
        />
        {/* Nothing to offer: say which country we have nothing for, so the merchant knows the
            empty dropdown is a missing dataset and not a broken screen. Falls back to a
            country-less wording when the store country itself couldn't be determined. */}
        {available.length === 0 && (
          <Text fontSize="caption" color="danger-textLow">
            {country
              ? t('designer.coverage.noneForCountry', {
                  country: countryName(country, i18n.language),
                })
              : t('designer.coverage.noneUnknownCountry')}
          </Text>
        )}
        {selected && (
          <Text fontSize="caption" color="neutral-textLow">
            {`${t('designer.coverage.appliesTo')}: ${selected.fields
              .map((f) => fieldLabel[f])
              .join(' → ')}`}
          </Text>
        )}
      </CollapsibleSection>
    </Box>
  );
}
